import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Eye, EyeOff, Pencil, Trash2, Save, X } from "lucide-react";
import api, { formatErr } from "../../lib/api";

export default function AdmReviews() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // review object being edited (or null)

  const refresh = () => api.get("/admin/reviews").then((r) => setList(r.data));
  useEffect(() => { refresh(); }, []);

  const toggleApproved = async (r) => {
    try { await api.put(`/admin/reviews/${r.id}`, { approved: !r.approved }); toast.success("Updated"); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete review by ${r.user_name}?`)) return;
    try { await api.delete(`/admin/reviews/${r.id}`); toast.success("Deleted"); refresh(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/reviews/${editing.id}`, {
        title: editing.title, body: editing.body, rating: editing.rating, approved: editing.approved,
      });
      toast.success("Saved"); setEditing(null); refresh();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div><div className="overline mb-2">Voice of Customer</div><h1 className="font-display text-4xl">Reviews</h1></div>

      <div className="space-y-3">
        {list.map((r) => (
          <div key={r.id} className="card-luxe p-5 flex justify-between gap-4" data-testid={`review-${r.id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <span>{r.user_name}</span>
                <span className="flex text-copper">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} size={12} className="fill-copper"/>)}</span>
                <span className="text-xs text-white/40">{r.created_at?.slice(0, 10)}</span>
              </div>
              <div className="font-display text-lg mt-1">{r.title}</div>
              <p className="text-sm text-white/60 mt-1 whitespace-pre-line">{r.body}</p>
            </div>
            <div className="flex flex-col gap-2 self-start shrink-0">
              <button data-testid={`review-toggle-${r.id}`} onClick={() => toggleApproved(r)} className="btn-outline text-xs">
                {r.approved ? <><Eye size={12}/> Approved</> : <><EyeOff size={12}/> Hidden</>}
              </button>
              <button data-testid={`review-edit-${r.id}`} onClick={() => setEditing({ ...r })} className="btn-outline text-xs"><Pencil size={12}/> Edit</button>
              <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r)} className="text-xs uppercase tracking-luxe px-3 py-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 flex items-center gap-1.5"><Trash2 size={12}/> Delete</button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="card-luxe p-10 text-center text-white/40">No reviews yet</div>}
      </div>

      {editing && (
        <div data-testid="review-edit-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 maintenance-blur" onClick={() => setEditing(null)}>
          <div className="card-luxe p-6 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div className="font-display text-2xl">Edit Review</div>
              <button onClick={() => setEditing(null)} className="p-2 hover:text-copper"><X size={16}/></button>
            </div>
            <div className="space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Rating</div>
                <select data-testid="review-edit-rating" value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: parseInt(e.target.value) })} className="input-box">
                  {[1,2,3,4,5].map((n) => <option key={n} value={n}>{n} ★</option>)}
                </select>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Title</div>
                <input data-testid="review-edit-title" className="input-box" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })}/>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Body</div>
                <textarea data-testid="review-edit-body" className="input-box" rows={5} value={editing.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })}/>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid="review-edit-approved" checked={!!editing.approved} onChange={(e) => setEditing({ ...editing, approved: e.target.checked })}/> Approved (visible on storefront)
              </label>
            </div>
            <div className="flex gap-3 mt-5">
              <button data-testid="review-edit-save" onClick={saveEdit} className="btn-primary"><Save size={13}/> Save</button>
              <button onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
